import { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  History,
  Loader2,
  XCircle,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { formatDateTime } from '../../components/reporting/format';
import type { ExportJobRow } from '../../components/reporting/types';

/**
 * Page `/reporting/historique` (US-REP-05).
 *
 *  Liste paginée des exports enregistrés dans `export_jobs`. Colonnes :
 *  type (module), format, statut, filtres (JSON), date de création, date de
 *  complétion, utilisateur. Aucun téléchargement disponible (les fichiers
 *  ne sont pas conservés en MVP).
 */

interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
}

const STATUT_LABELS: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  queued: { label: 'En file', color: 'bg-gray-100 text-gray-700', icon: Clock },
  running: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  completed: { label: 'Terminé', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const TYPE_LABELS: Record<string, string> = {
  equipements: 'Équipements',
  interventions: 'Interventions',
  batiments: 'Bâtiments',
  services: 'Services',
  personnels: 'Personnels',
  audit_logs: 'Audit / Dashboard',
  dashboard: 'Dashboard',
  infrastructure: 'Infrastructure',
};

export function ExportJobsPage() {
  const [items, setItems] = useState<ExportJobRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: ExportJobRow[]; pagination: PaginationInfo }>(
        `/reporting/export-jobs?page=${page}&page_size=50`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.page_size))
    : 1;

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <History className="w-7 h-7 mr-2 text-primary-600" />
          Historique des exports
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Traçabilité des exports réalisés depuis le module Reporting. Les fichiers eux-mêmes ne
          sont pas conservés (génération à la demande).
        </p>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>Type</Th>
                <Th>Format</Th>
                <Th>Statut</Th>
                <Th>Filtres</Th>
                <Th>Utilisateur</Th>
                <Th>Demandé le</Th>
                <Th>Terminé le</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucun export enregistré.
                  </td>
                </tr>
              ) : (
                items.map((job) => {
                  const statut = STATUT_LABELS[job.statut] ?? {
                    label: job.statut,
                    color: 'bg-gray-100 text-gray-700',
                    icon: Clock,
                  };
                  const StatutIcon = statut.icon;
                  const typeLabel = TYPE_LABELS[job.module] ?? job.module;
                  return (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                        {typeLabel}
                      </td>
                      <td className="px-3 py-2 text-gray-700 uppercase">{job.format}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${statut.color}`}
                        >
                          <StatutIcon className="w-3 h-3 mr-1" />
                          {statut.label}
                        </span>
                      </td>
                      <td
                        className="px-3 py-2 text-gray-600 max-w-[280px] truncate"
                        title={JSON.stringify(job.filters)}
                      >
                        <code className="text-[11px] bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                          {formatFilters(job.filters)}
                        </code>
                      </td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-[11px]">
                        {job.user_id.slice(0, 8)}…
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {formatDateTime(job.created_at)}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {formatDateTime(job.completed_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-gray-200 text-sm">
            <span className="text-gray-500">
              Page {pagination.page} sur {totalPages} — {pagination.total} export(s)
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={pagination.page === totalPages}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatFilters(filters: unknown): string {
  if (!filters || typeof filters !== 'object') return '—';
  const entries = Object.entries(filters as Record<string, unknown>).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );
  if (entries.length === 0) return '—';
  return entries
    .map(([k, v]) => {
      const val = typeof v === 'string' ? v : JSON.stringify(v);
      return `${k}=${val}`;
    })
    .join(' · ');
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}
